// View / edit a single pricing row for a business entity.
// Route: /pricing/be/details/:id/:action/:operationGroup/:operation
import { useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import {
  useBusinessEntityDetailsQuery,
  useBusinessEntityGridsQuery,
  useUpdateBusinessEntityGridsMutation,
  useSetDefaultPricingForBEMutation,
} from './api';
import { PricingForm } from '@/features/pricing-global/components/PricingForm';
import {
  OperationActionEnum,
  OperationGroupEnum,
  InternalFeeOperationTypeEnum,
  InternalRevenueOperationTypeEnum,
  isInternalGroup,
} from '@/types/pricing';
import type { PricingPayloadModel } from '@/types/pricing';

interface RouteParams {
  id: string;
  action: string;
  operationGroup: string;
  operation: string;
}

export function BusinessEntityPricingUpdatePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const rawParams = useParams() as unknown as RouteParams;
  const { id, action, operationGroup, operation } = rawParams;

  const beId = Number(id);
  const parsedGroup = operationGroup as OperationGroupEnum;
  const validAction = Object.values(OperationActionEnum).includes(action as OperationActionEnum);
  const validGroup = isInternalGroup(parsedGroup);
  const isRevenue = parsedGroup === OperationGroupEnum.INTERNAL_REVENUE;

  // Resolve operationType enum value from route param (always lowercase in route)
  const operationType = isRevenue
    ? (operation as InternalRevenueOperationTypeEnum)
    : (operation as InternalFeeOperationTypeEnum);

  useEffect(() => {
    if (!validAction || !validGroup || isNaN(beId)) {
      navigate(`/pricing/be/details/${id}`, { replace: true });
    }
  }, [validAction, validGroup, beId, navigate, id]);

  const detailsQuery = useBusinessEntityDetailsQuery(beId, validAction && validGroup);
  const gridsQuery = useBusinessEntityGridsQuery(
    beId,
    parsedGroup,
    operationType,
    validAction && validGroup,
  );

  const updateGrids = useUpdateBusinessEntityGridsMutation();
  const setDefault = useSetDefaultPricingForBEMutation();

  const isSaving = updateGrids.isPending || setDefault.isPending;

  const be = detailsQuery.data as { id: number; companyName?: string } | undefined;

  // Determine isDefaultPricing: if gridsQuery returned null/undefined the BE uses global default
  const rawFormData = gridsQuery.data ?? null;
  const isDefaultPricing = rawFormData === null || rawFormData === undefined;
  const formData: PricingPayloadModel | null = rawFormData
    ? { ...rawFormData, isDefaultPricing }
    : null;

  const handleSave = async (payload: PricingPayloadModel) => {
    try {
      const result = await updateGrids.mutateAsync({ beId, operationGroup: parsedGroup, payload });
      if ((result as { result?: { businessEntityId?: number } }).result?.businessEntityId) {
        navigate(
          `/pricing/be/details/${(result as { result: { businessEntityId: number } }).result.businessEntityId}`,
        );
      } else {
        navigate(`/pricing/be/details/${beId}`);
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : t('messages.error', { defaultValue: 'Error' });
      await Swal.fire(t('messages.error', { defaultValue: 'Error' }), message, 'error');
    }
  };

  const handleSelectDefaultPricing = async () => {
    try {
      await setDefault.mutateAsync({
        businessEntityId: beId,
        operationType,
        isRevenueOperation: isRevenue,
      });
      navigate(`/pricing/be/details/${beId}`);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : t('messages.error', { defaultValue: 'Error' });
      await Swal.fire(t('messages.error', { defaultValue: 'Error' }), message, 'error');
    }
  };

  const handleChangeAction = (nextAction: OperationActionEnum) => {
    navigate(
      `/pricing/be/details/${id}/${nextAction}/${operationGroup}/${operation}`,
      { replace: true },
    );
  };

  const isLoading = detailsQuery.isLoading || gridsQuery.isLoading;

  return (
    <Box>
      <Paper sx={{ p: 3, minHeight: '70vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Typography variant="h6">
            {t('pages.pricing.be_pricing.title', { defaultValue: 'Business Entity Pricing' })}
            {' — '}
            {t(`pages.pricing.be_pricing.${action}_entity_details`, {
              defaultValue: `${action} entity details`,
            })}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/pricing/be/details/${id}`)}
          >
            {t('global.buttons.back_to_overview', { defaultValue: 'Back to overview' })}
          </Button>
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          {be?.companyName ?? 'N/A'}
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          {t(`pages.pricing.operation_type.${operation}`, { defaultValue: operation })}
        </Typography>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <PricingForm
            mode="business-entity"
            businessEntityId={beId}
            formData={formData}
            action={action as OperationActionEnum}
            operationType={operation}
            operationGroup={parsedGroup}
            isRevenueData={isRevenue}
            isDefaultPricing={isDefaultPricing}
            onSubmit={(data) => void handleSave(data)}
            onChangeAction={handleChangeAction}
            onSelectDefaultPricing={() => void handleSelectDefaultPricing()}
            isSaving={isSaving}
          />
        )}
      </Paper>
    </Box>
  );
}
